import { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import CustomFormDrawer from '../customFormDrawer';
import {
  API_ACTION_PLANS,
  API_ACTION_PLANS_PROPOSAL,
  api_action_plan,
} from '../../../constants/apiRoutes';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { ACTION_PLAN_FIELDS } from '../../../constants/actionPlan';
import http from '../../../utility/http';
import { hasScopedAuthPermissionCheck } from '../../../utility/permissions';
import {
  getObjectToNumberArray,
  isObjectEmpty,
  trimString,
} from '../../../utility/helpers';
import { showSuccessMessage } from '../../../utility/uiUtils';
import { prepareTasksForSave } from '../../../utility/tasks';
import {
  INITIAL_DATA,
  getAddActionPlanFields,
  getEditActionPlanFields,
  getInitialData,
  getInitialEditData,
  getEditActionPlanSuccessMessage,
  getTitle,
} from './config';

const {
  DESCRIPTION,
  DUE_DATE,
  STATUS,
  USER,
  TAGS,
  TASKS,
  TITLE,
  PROGRESS,
  COURSES,
  IS_PROPOSED,
  IS_PENDING_PROPOSAL,
  CREATOR,
} = ACTION_PLAN_FIELDS;

const useStyles = makeStyles(() => ({
  form: {
    display: 'grid',
    gridColumnGap: 24,
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridTemplateAreas: `
      "user ."  
      "title ."
      "date ."
      "status progress"
      "tags courses"
      "description description"
      "tasks tasks"
      "creator created"
      "notify notify"
    `,
  },
  title: {
    gridArea: 'title',
  },
  user: {
    gridArea: 'user',
  },
  progress: {
    gridArea: 'progress',
  },
  dueDate: {
    gridArea: 'date',
  },
  status: {
    gridArea: 'status',
  },
  tags: {
    gridArea: 'tags',
  },
  // notify: {
  //   gridArea: 'notify',
  // },
  description: {
    gridArea: 'description',
  },
  tasks: {
    gridArea: 'tasks',
  },
  creator: {
    gridArea: 'creator',
  },
  createdOn: {
    gridArea: 'created',
  },
  courses: {
    gridArea: 'courses',
  },
}));

const ManageActionPlan = ({
  translations,
  isOpen,
  isInitialValid = false,
  hasCreatedForField = false,
  auth = {},
  accessControl,
  isPlanProposal = false,
  actionPlan = {},
  statuses = [],
  courses = [],
  categories,
  user = {},
  allEmployees = [],
  managableEmployees = [],
  onCreateTag,
  getCourses,
  getCourseEnrolledUsers,
  onCoursesSearch,
  onClose,
  onDelete = () => {},
  onConfirm,
  canSeeCourses = false,
}) => {
  const classes = useStyles();
  const [initialData, setInitialData] = useState(INITIAL_DATA);
  const isCreate = useMemo(() => isObjectEmpty(actionPlan), [actionPlan]);
  const hasCreator = !!actionPlan[CREATOR];
  const isProposed = actionPlan?.[IS_PROPOSED];
  const isPendingProposal = actionPlan?.[IS_PENDING_PROPOSAL];
  const isCreatedForCurrentUser = actionPlan?.[USER]?.id === auth?.id;
  const isDeletable = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PLANS_DELETE,
    accessControl,
    auth?.id,
    actionPlan?.[USER]?.id
  );

  const canManageCourseUserCheck = userId =>
    canSeeCourses &&
    hasScopedAuthPermissionCheck(
      PERMISSION_CODES.LEARNING_MANAGE_USERS,
      accessControl,
      auth?.id,
      userId
    );

  const updateInitialData = useCallback(() => {
    let data = {
      ...INITIAL_DATA,
    };

    if (!isObjectEmpty(user)) {
      data = getInitialData(user);
    }
    if (!isObjectEmpty(actionPlan)) {
      data = getInitialEditData(actionPlan);
    }

    setInitialData(data);
  }, [user, actionPlan]);

  useEffect(() => {
    if (isOpen && (actionPlan?.id || user)) {
      updateInitialData();
    }
  }, [isOpen, actionPlan, user, updateInitialData]);

  const handleSave = values => {
    const data = {
      [TITLE]: trimString(values[TITLE]),
      [USER]: values[USER]?.id,
      [DESCRIPTION]: values[DESCRIPTION],
      [DUE_DATE]: values[DUE_DATE],
      [STATUS]: values[STATUS],
      // [NOTIFY]: values[NOTIFY],
      [TAGS]: values[TAGS],
      [TASKS]: prepareTasksForSave(values[TASKS]),
      [PROGRESS]: values[PROGRESS],
      [COURSES]: canManageCourseUserCheck(values[USER]?.id)
        ? getObjectToNumberArray(values[COURSES])
        : [],
      [IS_PROPOSED]: isPlanProposal,
      ...(isPlanProposal ? { [IS_PENDING_PROPOSAL]: true } : {}),
    };

    return http
      .post(isPlanProposal ? API_ACTION_PLANS_PROPOSAL : API_ACTION_PLANS, data)
      .then(() => {
        showSuccessMessage(
          isPlanProposal
            ? translations.successProposal
            : translations.successAdd
        );
        onConfirm(data);
      });
  };

  const handleEditSave = async updatedPoint => {
    const data = {
      [TITLE]: trimString(updatedPoint[TITLE]),
      [USER]: updatedPoint[USER]?.id,
      [DESCRIPTION]: updatedPoint[DESCRIPTION],
      [STATUS]: updatedPoint[STATUS],
      // [NOTIFY]: updatedPoint[NOTIFY],
      [DUE_DATE]: updatedPoint[DUE_DATE],
      [TAGS]: updatedPoint[TAGS],
      [TASKS]: prepareTasksForSave(updatedPoint[TASKS]),
      [PROGRESS]: updatedPoint[PROGRESS],
      [COURSES]: getObjectToNumberArray(updatedPoint[COURSES]),
      ...(isProposed && isPendingProposal && !isCreatedForCurrentUser
        ? { [CREATOR]: auth.id, [IS_PENDING_PROPOSAL]: false }
        : {}),
    };

    return http.patch(api_action_plan(actionPlan.id), data).then(() => {
      showSuccessMessage(
        getEditActionPlanSuccessMessage(translations, actionPlan, auth)
      );
      onConfirm(data);
    });
  };

  return (
    <CustomFormDrawer
      customFormClass={classes.form}
      translations={translations}
      titleText={getTitle(
        translations,
        isPlanProposal,
        isPendingProposal,
        isCreatedForCurrentUser
      )}
      isOpened={isOpen}
      isInitialValid={
        isPendingProposal ? isCreatedForCurrentUser : isInitialValid
      }
      fields={
        isCreate
          ? getAddActionPlanFields(
              classes,
              // translations,
              // currentUser,
              user,
              hasCreatedForField,
              isPlanProposal,
              onCreateTag,
              getCourses,
              getCourseEnrolledUsers,
              onCoursesSearch,
              canManageCourseUserCheck
            )
          : getEditActionPlanFields(
              classes,
              // translations,
              // currentUser,
              hasCreatedForField,
              // actionPlan,
              actionPlan[IS_PENDING_PROPOSAL] &&
                actionPlan[USER]?.id === auth?.id,
              hasCreator,
              onCreateTag,
              getCourses,
              getCourseEnrolledUsers,
              onCoursesSearch,
              canManageCourseUserCheck
            )
      }
      allEmployees={allEmployees}
      managableEmployees={managableEmployees}
      categories={categories}
      courses={courses}
      initialData={initialData}
      statuses={statuses}
      hideDelete={!isDeletable}
      onClose={onClose}
      onSave={isCreate ? handleSave : handleEditSave}
      onDelete={onDelete}
      hasCancelButton
      isMiddleWidth
    />
  );
};

ManageActionPlan.propTypes = {
  translations: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  allEmployees: PropTypes.arrayOf(PropTypes.shape({})),
  managableEmployees: PropTypes.arrayOf(PropTypes.shape({})),
  user: PropTypes.object,
  auth: PropTypes.object,
  hasCreatedForField: PropTypes.bool,
  statuses: PropTypes.arrayOf(PropTypes.shape({})),
  courses: PropTypes.arrayOf(PropTypes.shape({})),
  actionPlan: PropTypes.object,
  isPlanProposal: PropTypes.bool,
  canSeeCourses: PropTypes.bool,
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isInitialValid: PropTypes.bool,
  onDelete: PropTypes.func,
  getCourses: PropTypes.func.isRequired,
  getCourseEnrolledUsers: PropTypes.func.isRequired,
  onCoursesSearch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCreateTag: PropTypes.func.isRequired,
};

export default ManageActionPlan;
